import type { Filters } from '@/types'

import { useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import { emptyFilters, filtersToQuery } from '@/lib/search'

export type SearchDraft = Pick<Filters, 'q' | 'area' | 'types' | 'minPrice' | 'maxPrice' | 'beds'>

export const emptyDraft: SearchDraft = {
    q: '',
    area: '',
    types: [],
    minPrice: null,
    maxPrice: null,
    beds: 0
}

/*
 * Draft state shared by the hero search bar and the search window.
 * Submitting sends the visitor to the search page with the choices in the address.
 */
export const useSearchDraft = (initial: Partial<SearchDraft> = {}) => {
    const router = useRouter()
    const [draft, setDraft] = useState<SearchDraft>({ ...emptyDraft, ...initial })

    const update = useCallback((patch: Partial<SearchDraft>) => {
        setDraft(current => ({ ...current, ...patch }))
    }, [])

    const reset = useCallback((next: Partial<SearchDraft> = {}) => {
        setDraft({ ...emptyDraft, ...next })
    }, [])

    const submit = useCallback(async (keep: Partial<Filters> = {}) => {
        const query = filtersToQuery({ ...emptyFilters, ...keep, ...draft })
        const onSearchPage = router.pathname === '/homes'

        await router.push({ pathname: '/homes', query }, undefined, { shallow: onSearchPage, scroll: !onSearchPage })
    }, [draft, router])

    return { draft, update, reset, submit }
}
